import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiMenu, FiX, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  Root,
  Container,
  ContainerCategoty,
  CategoryItem,
  SubcategoryCarouselItem,
  AllProductsItem,
  HamburgerButton,
  Separator,
  Sidebar,
  SidebarHeader,
  SidebarTitle,
  SidebarContent,
  SidebarItem,
  SidebarCategoryWrapper,
  SidebarSubcategoryList,
  SidebarSubcategoryItem,
  ContainerBorder
} from './styles';

import { StoreState } from '../../store/createStore';
import {
  findProductRequest,
  setProductFilter,
} from '../../store/modules/product/actions';
import { routesEndpoints } from '../../routes/endpoint';
import { findCategoryRequest, searchComponents, setSubcategories, setCategorySelected, setSubCategorySelected, resetCategoriesSelected } from '../../store/modules/category/actions';
import { config } from '../../config';
import { useScroll } from '../../hooks/ScrollContext';
import { Carousel, CarouselHandle } from '../../components/Carousel';
import { CategoryData } from '../../store/modules/category/types';

const Menu: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categoryId = queryParams.get('category');
  const subcategoryId = queryParams.get('subcategory');
  const dispatch = useDispatch();
  const history = useHistory();
  const { categorys, subCategoriesSelected, categorySelected, subCategorySelected } = useSelector((state: StoreState) => state.category);
  const { products } = useSelector((state: StoreState) => state.product);
  const { multistoreSelected } = useSelector((state: StoreState) => state.parameter);
  const { scrollToTop } = useScroll();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);
  const [isLargeScreen, setIsLargeScreen] = useState<boolean>(false);
  const [sidebarLeft, setSidebarLeft] = useState<number>(0);
  
  const sidebarItemsRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const sidebarSubcategoryItemsRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const carouselRef = useRef<CarouselHandle>(null);
  const subcategoryCarouselRef = useRef<CarouselHandle>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const hamburgerButtonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const getCategoryIndex = useCallback((categoryId: string) => {
    if (categoryId === '') return 0;
    const index = categorys.findIndex(cat => cat.id === categoryId);
    return index >= 0 ? index + 1 : -1;
  }, [categorys]);

  const getSubcategoryIndex = useCallback((subcategoryId: string) => {
    const index = subCategoriesSelected.findIndex(sub => sub.id === subcategoryId);
    return index >= 0 ? index : -1;
  }, [subCategoriesSelected]);

  const handledFindProductByCategory = useCallback(
    (category: CategoryData, fromSidebar = false, isSubCategory = false, keepSidebarOpen = false) => {
      let filterSearch = '';
      if (isSubCategory) {
        filterSearch = `?category=${categorySelected}&subcategory=${category.id}`;
      } else {
          filterSearch = `?category=${category.id}`;
      }
      scrollToTop();
      history.push(`${routesEndpoints.HOME}${filterSearch}`);
      
      if (fromSidebar && !isSubCategory && carouselRef.current) {
        const index = getCategoryIndex(category.id);
        if (index >= 0) {
          setTimeout(() => {
            carouselRef.current?.scrollToIndex(index);
          }, 100);
        }
      }
      
      if (fromSidebar && isSubCategory && subcategoryCarouselRef.current) {
        const index = getSubcategoryIndex(category.id);
        if (index >= 0) {
          setTimeout(() => {
            subcategoryCarouselRef.current?.scrollToIndex(index);
          }, 100);
        }
      }
      
      if (!keepSidebarOpen) {
        setSidebarOpen(false);
      }
    },
    [history, categorySelected, scrollToTop, getCategoryIndex, getSubcategoryIndex],
  );


  const handleShowAllProducts = useCallback((fromSidebar = false) => {
    dispatch(resetCategoriesSelected());
    if (categorySelected !== '' || products.length === 0) {
      dispatch(
        setProductFilter({
          categoryId: '',
        }),
      );
      dispatch(findProductRequest());
    }
    scrollToTop();
    history.push(routesEndpoints.HOME);
    
    if (fromSidebar && carouselRef.current) {
      setTimeout(() => {
        carouselRef.current?.scrollToIndex(0);
      }, 100);
    }
    
    setSidebarOpen(false);
  }, [dispatch, history, categorySelected, products.length, scrollToTop]);

  useEffect(() => {
    dispatch(findCategoryRequest());
  }, [dispatch]);

  useEffect(() => {
    if (categoryId) {
      dispatch(setCategorySelected(categoryId));
      dispatch(
        setProductFilter({
          categoryId: categoryId,
        }),
      );
    } else if (!categoryId && !subcategoryId) {
      // Resetar quando não há query params
      dispatch(resetCategoriesSelected());
    }

    if (subcategoryId) {
      dispatch(setSubCategorySelected(subcategoryId));
      dispatch(
        setProductFilter({
          categoryId: subcategoryId,
        }),
      );
    }
    if (multistoreSelected.store_id) {
      dispatch(findProductRequest());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, subcategoryId, dispatch, multistoreSelected.store_id]);

  useEffect(() => {
    if (categoryId) {
      const categorySelected = categorys.find(category => category.id === categoryId);
      dispatch(setSubcategories(categorySelected?.subcategories || []));
    }
  }, [categorys, categoryId, dispatch]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    
    if (categorys.length > 0 && carouselRef.current) {
      const index = categoryId ? getCategoryIndex(categoryId) : 0;
      if (index >= 0) {
        timeoutId = setTimeout(() => {
          carouselRef.current?.scrollToIndex(index);
        }, 300);
      }
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [categoryId, categorys, getCategoryIndex]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    
    if (subcategoryId && subCategoriesSelected.length > 0 && subcategoryCarouselRef.current) {
      const index = getSubcategoryIndex(subcategoryId);
      if (index >= 0) {
        timeoutId = setTimeout(() => {
          subcategoryCarouselRef.current?.scrollToIndex(index);
        }, 300);
      }
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [subcategoryId, subCategoriesSelected, getSubcategoryIndex]);

  useEffect(() => {
    dispatch(searchComponents(subcategoryId || categoryId));
  }, [dispatch, categoryId, subcategoryId]);

  useEffect(() => {
    if (!sidebarOpen) return;

    let timeoutId1: NodeJS.Timeout | null = null;
    let timeoutId2: NodeJS.Timeout | null = null;

    timeoutId1 = setTimeout(() => {
      const categoryKey = categorySelected === '' ? 'all' : categorySelected;
      const sidebarItem = sidebarItemsRef.current[categoryKey];
      if (sidebarItem) {
        sidebarItem.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center'
        });
      }
      
      if (subCategorySelected) {
        timeoutId2 = setTimeout(() => {
          const subcategoryItem = sidebarSubcategoryItemsRef.current[subCategorySelected];
          if (subcategoryItem) {
            subcategoryItem.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center'
            });
          }
        }, 200);
      }
    }, 100);

    return () => {
      if (timeoutId1) clearTimeout(timeoutId1);
      if (timeoutId2) clearTimeout(timeoutId2);
    };
  }, [sidebarOpen, categorySelected, subCategorySelected]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        hamburgerButtonRef.current &&
        !hamburgerButtonRef.current.contains(event.target as Node)
      ) {
        setSidebarOpen(false);
      }
    };

    if (sidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarOpen]);

  useEffect(() => {
    if (categorySelected) {
      const category = categorys.find(c => c.id === categorySelected);
      if (category?.subcategories && category.subcategories.length > 0) {
        setExpandedCategoryId(categorySelected);
      } else {
        setExpandedCategoryId(null);
      }
    } else {
      setExpandedCategoryId(null);
    }
  }, [categorySelected, categorys]);

  useEffect(() => {
    const calculatePosition = () => {
      const isLarge = window.innerWidth > 1024;
      setIsLargeScreen(isLarge);
      
      if (sidebarOpen && isLarge && hamburgerButtonRef.current) {
        const buttonRect = hamburgerButtonRef.current.getBoundingClientRect();
        const buttonEnd = buttonRect.right;
        const sidebarWidth = 450;
        const calculatedLeft = Math.max(0, buttonEnd - sidebarWidth);
        setSidebarLeft(calculatedLeft);
      } else {
        setSidebarLeft(0);
      }
    };

    calculatePosition();
    window.addEventListener('resize', calculatePosition);
    
    return () => {
      window.removeEventListener('resize', calculatePosition);
    };
  }, [sidebarOpen]);

  return (
    <Root>
      <ContainerBorder>
      <Container ref={containerRef}>
        <HamburgerButton ref={hamburgerButtonRef} onClick={() => setSidebarOpen(true)}>
          <FiMenu size={24} color="black" />
          <span>Categorias</span>
        </HamburgerButton>

        <Separator />

        <Sidebar 
          ref={sidebarRef} 
          open={sidebarOpen}
          isLargeScreen={isLargeScreen}
          leftPosition={sidebarLeft}
        >
          <SidebarHeader>
            <SidebarTitle>Categorias</SidebarTitle>
            <HamburgerButton onClick={() => setSidebarOpen(false)}>
              <FiX size={24} />
            </HamburgerButton>
          </SidebarHeader>
          <SidebarContent>
            <SidebarItem
              ref={(el) => (sidebarItemsRef.current['all'] = el)}
              onClick={() => handleShowAllProducts(true)}
              selected={categorySelected === ''}
            >
              <span>TODOS OS PRODUTOS</span>
            </SidebarItem>
            {categorys.map((category) => {
              const hasSubcategories = category.subcategories && category.subcategories.length > 0;
              const isExpanded = expandedCategoryId === category.id;
              
              return (
                <SidebarCategoryWrapper key={category.id}>
                  <SidebarItem
                    ref={(el) => (sidebarItemsRef.current[category.id] = el)}
                    onClick={(e) => {
                      if (hasSubcategories) {
                        e.stopPropagation();
                        if (isExpanded) {
                          setExpandedCategoryId(null);
                        } else {
                          handledFindProductByCategory(category, true, false, true);
                          setExpandedCategoryId(category.id);
                        }
                      } else {
                        handledFindProductByCategory(category, true);
                      }
                    }}
                    selected={categorySelected === category.id}
                  >
                    <span>{category.short_description}</span>
                    {hasSubcategories && (
                      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                        {isExpanded ? (
                          <FiChevronDown size={16} />
                        ) : (
                          <FiChevronRight size={16} />
                        )}
                      </div>
                    )}
                  </SidebarItem>
                  {isExpanded && hasSubcategories && (
                    <SidebarSubcategoryList>
                      {category.subcategories?.map((subcategory) => (
                        <SidebarSubcategoryItem
                          key={subcategory.id}
                          ref={(el) => (sidebarSubcategoryItemsRef.current[subcategory.id] = el)}
                          onClick={() => handledFindProductByCategory(subcategory, true, true)}
                          selected={subCategorySelected === subcategory.id}
                        >
                          <span>{subcategory.short_description}</span>
                        </SidebarSubcategoryItem>
                      ))}
                    </SidebarSubcategoryList>
                  )}
                </SidebarCategoryWrapper>
              );
            })}
          </SidebarContent>
        </Sidebar>

        <ContainerCategoty>
          <Carousel ref={carouselRef}>
            {[
              <AllProductsItem
                key="all"
                onClick={handleShowAllProducts}
                selected={categorySelected === ''}
              >
                <span>TODOS OS PRODUTOS</span>
              </AllProductsItem>,
            ].concat(
              categorys.map((category) => (
                <div
                  key={category.id}
                >
                  <CategoryItem
                    onClick={() => handledFindProductByCategory(category)}
                    selected={categorySelected === category.id}
                  >
                    <span>{category.short_description}</span>
                  </CategoryItem>
                </div>
              )),
            )}
          </Carousel>
        </ContainerCategoty>
      </Container>
      </ContainerBorder>
      {
        subCategoriesSelected.length > 0 && (
          <ContainerBorder>
          <Container>
            <ContainerCategoty>
              <Carousel ref={subcategoryCarouselRef}>
                {
                  subCategoriesSelected.map((subcategory) => (
                    <div
                      key={subcategory.id}
                    >
                      <SubcategoryCarouselItem
                        onClick={() => handledFindProductByCategory(subcategory, false, true)}
                        selected={subCategorySelected === subcategory.id}
                      >
                        <span>{subcategory.short_description}</span>
                      </SubcategoryCarouselItem>
                    </div>
                  ))
                }
              </Carousel>
            </ContainerCategoty>
          </Container>
          </ContainerBorder>
        )
      }
    </Root>
  );
};

const MenuFactory: React.FC = () => {
  const { multistoreSelected } = useSelector(
    (state: StoreState) => state.parameter,
  );

  if (config.isAllowedMultistore && !multistoreSelected.store_id) {
    return <></>;
  }

  return <Menu />;
};

export default MenuFactory;
